import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Truck, Plus, Search, Clock, CheckCircle, Phone, Package, Navigation, Map, ShoppingCart, Info, ArrowLeft, X, User, RotateCcw, Eye, Edit2, XCircle, Archive, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import { employeeService } from '../../services/employeeService';
import { cartonService } from '../../services/cartonService';
import { supabase } from '../../lib/supabase';
import DepotDetailModal from './components/DepotDetailModal';
import DepotEditModal from './components/DepotEditModal';
import DepotActionModal from './components/DepotActionModal';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  scheduled: { label: 'Planifié', color: 'bg-blue-100 text-blue-800' },
  assigned: { label: 'Affecté', color: 'bg-purple-100 text-purple-800' },
  in_progress: { label: 'En cours', color: 'bg-orange-100 text-orange-800' },
  collected: { label: 'Déposé', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800' },
  archived: { label: 'Archivé', color: 'bg-gray-100 text-gray-600' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Basse', color: 'bg-gray-100 text-gray-700' },
  normal: { label: 'Normale', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'Haute', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700' },
};

export default function BarrelDepositSchedulingSystem() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cartonInventory, setCartonInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showNewDepositModal, setShowNewDepositModal] = useState(false);
  const [showRouteOptimization, setShowRouteOptimization] = useState(false);
  const [selectedDeposits, setSelectedDeposits] = useState([]);
  const [showArchives, setShowArchives] = useState(false);
  const [toast, setToast] = useState(null);

  // Action modals
  const [detailDeposit, setDetailDeposit] = useState(null);
  const [editDeposit, setEditDeposit] = useState(null);
  const [actionModal, setActionModal] = useState(null); // { deposit, actionType }

  const [newDeposit, setNewDeposit] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    customerCity: '',
    barrelType: 'noir_270l',
    quantity: 1,
    scheduledDate: new Date()?.toISOString()?.split('T')?.[0],
    priority: 'normal',
    deliveryNotes: '',
    driverId: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [depositsData, driversData, cartonsData] = await Promise.all([
        barrelService?.getBarrelDeposits(),
        employeeService?.getAll({ isActive: true }),
        cartonService?.getCartonInventory()
      ]);
      setDeposits(depositsData);
      setDrivers(driversData?.filter(d => d?.role === 'chauffeur'));
      setCartonInventory(cartonsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('error', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeposit = async () => {
    try {
      const inventory = await barrelService?.getInventory();
      const barrelPrice = inventory?.find(i => i?.barrelType === newDeposit?.barrelType)?.priceEuros || 35;
      const totalPrice = parseInt(newDeposit?.quantity) * barrelPrice;

      const depositData = {
        client_name: newDeposit?.customerName,
        client_phone: newDeposit?.customerPhone,
        client_address: newDeposit?.customerAddress,
        client_city: newDeposit?.customerCity,
        barrel_type: newDeposit?.barrelType,
        quantity: parseInt(newDeposit?.quantity),
        total_price: totalPrice,
        planned_collection_date: newDeposit?.scheduledDate,
        collection_status: 'pending',
        priority: newDeposit?.priority,
        delivery_notes: newDeposit?.deliveryNotes,
        ...(newDeposit?.driverId ? { assigned_driver_id: newDeposit?.driverId, collection_status: 'scheduled' } : {})
      };

      await barrelService?.createBarrelDeposit(depositData);
      await loadData();
      setShowNewDepositModal(false);
      setNewDeposit({
        customerName: '', customerPhone: '', customerAddress: '', customerCity: '',
        barrelType: 'noir_270l', quantity: 1,
        scheduledDate: new Date()?.toISOString()?.split('T')?.[0],
        priority: 'normal', deliveryNotes: '', driverId: ''
      });
      showToast('success', 'Dépôt créé avec succès');
    } catch (error) {
      console.error('Error creating barrel deposit:', error);
      showToast('error', 'Erreur lors de la création du dépôt');
    }
  };

  const handleAssignDriver = async (depositId, driverId) => {
    try {
      await barrelService?.updateBarrelDeposit(depositId, {
        assigned_driver_id: driverId,
        collection_status: 'scheduled'
      });
      await loadData();
    } catch (error) {
      console.error('Error assigning driver:', error);
      showToast('error', 'Erreur lors de l\'assignation du chauffeur');
    }
  };

  const handleUpdateStatus = async (depositId, status) => {
    try {
      const updates = { collection_status: status };
      if (status === 'collected') {
        updates.actual_collection_date = new Date()?.toISOString()?.split('T')?.[0];
      }
      await barrelService?.updateBarrelDeposit(depositId, updates);
      await loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('error', 'Erreur lors de la mise à jour du statut');
    }
  };

  // Edit deposit
  const handleSaveEdit = async (depositId, formData) => {
    const updates = {
      client_name: formData?.client_name,
      client_phone: formData?.client_phone,
      client_address: formData?.client_address,
      client_city: formData?.client_city,
      quantity: formData?.quantity,
      planned_collection_date: formData?.planned_collection_date,
      priority: formData?.priority,
      assigned_driver_id: formData?.assigned_driver_id || null,
      collection_status: formData?.collection_status,
      delivery_notes: formData?.delivery_notes,
      admin_comment: formData?.admin_comment,
      updated_at: new Date()?.toISOString(),
    };
    const { error } = await supabase?.from('barrel_deposits')?.update(updates)?.eq('id', depositId);
    if (error) throw error;
    await loadData();
    showToast('success', 'Dépôt modifié avec succès');
  };

  // Handle Cancel / Archive / Delete
  const handleAction = async ({ depositId, saleId, actionType, reason, deleteLinkedSale }) => {
    try {
      if (actionType === 'cancel') {
        const { error } = await supabase?.from('barrel_deposits')?.update({
          collection_status: 'cancelled',
          is_cancelled: true,
          cancelled_at: new Date()?.toISOString(),
          cancelled_reason: reason || null,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', depositId);
        if (error) throw error;
        showToast('success', 'Dépôt annulé avec succès');
      } else if (actionType === 'archive') {
        const { error } = await supabase?.from('barrel_deposits')?.update({
          is_archived: true,
          archived_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', depositId);
        if (error) throw error;
        showToast('success', 'Dépôt archivé avec succès');
      } else if (actionType === 'delete') {
        // Delete related notifications
        await supabase?.from('admin_alerts')?.delete()?.eq('entity_id', depositId);
        // Delete the deposit
        const { error: depositError } = await supabase?.from('barrel_deposits')?.delete()?.eq('id', depositId);
        if (depositError) throw depositError;
        // Optionally delete linked sale
        if (deleteLinkedSale && saleId) {
          await supabase?.from('barrel_sales')?.delete()?.eq('id', saleId);
        }
        setDeposits(prev => prev?.filter(d => d?.id !== depositId));
        showToast('success', 'Dépôt supprimé définitivement');
        return;
      }
      await loadData();
    } catch (error) {
      console.error('Action error:', error);
      showToast('error', `Erreur : ${error?.message}`);
      throw error;
    }
  };

  const handleReactivateDeposit = async (depositId) => {
    try {
      await barrelService?.updateBarrelDeposit(depositId, {
        collection_status: 'pending',
        assigned_driver_id: null
      });
      await loadData();
      showToast('success', 'Dépôt réactivé');
    } catch (error) {
      console.error('Error reactivating deposit:', error);
      showToast('error', 'Erreur lors de la réactivation');
    }
  };

  const getBarrelTypeLabel = (type) => {
    if (type === 'noir_270l') return 'Barrique Noire 270L';
    if (type === 'bleu_220l') return 'Barrique Bleue 220L';
    const carton = cartonInventory?.find(c => c?.id === type || c?.cartonType === type);
    if (carton) return carton?.cartonType || carton?.description || type;
    return type;
  };

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cfg?.color}`}>
        {cfg?.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const cfg = PRIORITY_CONFIG?.[priority] || PRIORITY_CONFIG?.normal;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cfg?.color}`}>
        {cfg?.label}
      </span>
    );
  };

  const toggleDepositSelection = (depositId) => {
    setSelectedDeposits(prev =>
      prev?.includes(depositId) ? prev?.filter(id => id !== depositId) : [...prev, depositId]
    );
  };

  // Separate active vs archived
  const activeDeposits = deposits?.filter(d => !d?.is_archived);
  const archivedDeposits = deposits?.filter(d => d?.is_archived);

  const filteredDeposits = activeDeposits?.filter(deposit => {
    if (filterStatus !== 'all' && deposit?.collection_status !== filterStatus) return false;
    if (filterPriority !== 'all' && deposit?.priority !== filterPriority) return false;
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      return (
        deposit?.client_name?.toLowerCase()?.includes(search) ||
        deposit?.client_phone?.toLowerCase()?.includes(search) ||
        deposit?.client_address?.toLowerCase()?.includes(search) ||
        deposit?.client_city?.toLowerCase()?.includes(search)
      );
    }
    return true;
  });

  // Counters
  const pendingCount = activeDeposits?.filter(d => d?.collection_status === 'pending')?.length || 0;
  const scheduledCount = activeDeposits?.filter(d => d?.collection_status === 'scheduled')?.length || 0;
  const inProgressCount = activeDeposits?.filter(d => d?.collection_status === 'in_progress')?.length || 0;
  const collectedCount = activeDeposits?.filter(d => d?.collection_status === 'collected')?.length || 0;
  const cancelledCount = activeDeposits?.filter(d => d?.collection_status === 'cancelled')?.length || 0;
  const archivedCount = archivedDeposits?.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-white transition-all ${toast?.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast?.type === 'success' ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 flex-shrink-0" />}
          <span className="text-sm font-medium">{toast?.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 hover:opacity-75"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-green-600" />
            Système de Planification Dépôts Barriques & Cartons
          </h1>
          <p className="mt-2 text-gray-600">
            Planning complet des livraisons de barriques et cartons vides avec optimisation de routes et suivi en temps réel
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8 rounded-lg">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-900 mb-2">📦 Synchronisation Automatique</h3>
              <p className="text-sm text-green-800 mb-3">
                Les dépôts affichés ici sont <strong>automatiquement créés</strong> dès qu'une commande client est enregistrée dans le système. Aucune confirmation manuelle n'est nécessaire.
              </p>
              <button
                onClick={() => navigate('/barrel-customer-orders-management')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <ShoppingCart className="w-4 h-4" />
                Voir Commandes Clients
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-yellow-400">
            <p className="text-xs text-gray-500 mb-1">En attente</p>
            <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            <Clock className="w-6 h-6 text-yellow-400 mt-1" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-400">
            <p className="text-xs text-gray-500 mb-1">Planifiés</p>
            <p className="text-2xl font-bold text-blue-600">{scheduledCount}</p>
            <Calendar className="w-6 h-6 text-blue-400 mt-1" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-orange-400">
            <p className="text-xs text-gray-500 mb-1">En cours</p>
            <p className="text-2xl font-bold text-orange-600">{inProgressCount}</p>
            <Truck className="w-6 h-6 text-orange-400 mt-1" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-400">
            <p className="text-xs text-gray-500 mb-1">Déposés</p>
            <p className="text-2xl font-bold text-green-600">{collectedCount}</p>
            <CheckCircle className="w-6 h-6 text-green-400 mt-1" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-400">
            <p className="text-xs text-gray-500 mb-1">Annulés</p>
            <p className="text-2xl font-bold text-red-600">{cancelledCount}</p>
            <XCircle className="w-6 h-6 text-red-400 mt-1" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-gray-400">
            <p className="text-xs text-gray-500 mb-1">Archivés</p>
            <p className="text-2xl font-bold text-gray-600">{archivedCount}</p>
            <Archive className="w-6 h-6 text-gray-400 mt-1" />
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4 flex-1 w-full">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, téléphone, adresse..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="scheduled">Planifié</option>
                <option value="assigned">Affecté</option>
                <option value="in_progress">En cours</option>
                <option value="collected">Déposé</option>
                <option value="cancelled">Annulé</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="all">Toutes priorités</option>
                <option value="low">Basse</option>
                <option value="normal">Normale</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              {selectedDeposits?.length > 0 && (
                <button
                  onClick={() => setShowRouteOptimization(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Navigation className="w-5 h-5" />
                  Optimiser Route ({selectedDeposits?.length})
                </button>
              )}
              <button
                onClick={() => setShowNewDepositModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Nouveau Dépôt
              </button>
            </div>
          </div>
        </div>

        {/* Active Deposits Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-600" />
              Dépôts Actifs
              <span className="ml-2 bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">{filteredDeposits?.length}</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e?.target?.checked) setSelectedDeposits(filteredDeposits?.map(d => d?.id));
                        else setSelectedDeposits([]);
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barriques</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Planifiée</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priorité</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chauffeur</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDeposits?.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p>Aucun dépôt trouvé</p>
                    </td>
                  </tr>
                ) : (
                  filteredDeposits?.map((deposit) => (
                    <tr
                      key={deposit?.id}
                      className={`hover:bg-gray-50 ${deposit?.collection_status === 'cancelled' ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedDeposits?.includes(deposit?.id)}
                          onChange={() => toggleDepositSelection(deposit?.id)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <div className="text-sm font-medium text-gray-900">{deposit?.client_name}</div>
                        </div>
                        {deposit?.sale_id && (
                          <div className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <ShoppingCart className="w-3 h-3" />
                            Commande #{deposit?.sale_id?.slice(0, 8)}
                          </div>
                        )}
                        {deposit?.collection_status === 'cancelled' && (
                          <div className="text-xs text-red-600 mt-1 font-semibold">⚠ Annulé</div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {deposit?.client_phone}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 flex items-start gap-1">
                          <MapPin className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                          <div>
                            <div>{deposit?.client_address}</div>
                            <div className="text-gray-500">{deposit?.client_city}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{getBarrelTypeLabel(deposit?.barrel_type)}</div>
                        <div className="text-sm text-gray-500">Qté: {deposit?.quantity}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {deposit?.planned_collection_date ? new Date(deposit?.planned_collection_date)?.toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getPriorityBadge(deposit?.priority)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(deposit?.collection_status)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {deposit?.employees?.name || drivers?.find(d => d?.id === deposit?.assigned_driver_id)?.name || 'Non assigné'}
                        </div>
                      </td>
                      {/* ACTIONS COLUMN */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {/* Voir détails */}
                          <button
                            onClick={() => setDetailDeposit(deposit)}
                            title="Voir détails"
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {/* Modifier */}
                          <button
                            onClick={() => setEditDeposit(deposit)}
                            title="Modifier"
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {/* Annuler */}
                          {deposit?.collection_status !== 'cancelled' && deposit?.collection_status !== 'collected' && (
                            <button
                              onClick={() => setActionModal({ deposit, actionType: 'cancel' })}
                              title="Annuler"
                              className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          {/* Réactiver si annulé */}
                          {deposit?.collection_status === 'cancelled' && (
                            <button
                              onClick={() => handleReactivateDeposit(deposit?.id)}
                              title="Réactiver"
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          {/* Archiver */}
                          <button
                            onClick={() => setActionModal({ deposit, actionType: 'archive' })}
                            title="Archiver"
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                          {/* Supprimer */}
                          <button
                            onClick={() => setActionModal({ deposit, actionType: 'delete' })}
                            title="Supprimer définitivement"
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Archives Section */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <button
            onClick={() => setShowArchives(!showArchives)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Archive className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-bold text-gray-700">
                Archives
                <span className="ml-2 bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full">{archivedCount}</span>
              </h2>
            </div>
            <span className="text-sm text-gray-500">{showArchives ? '▲ Masquer' : '▼ Afficher'}</span>
          </button>

          {showArchives && (
            <div className="border-t border-gray-200">
              {archivedDeposits?.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <Archive className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p>Aucun dépôt archivé</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barriques</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date prévue</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Archivé le</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {archivedDeposits?.map((deposit) => (
                        <tr key={deposit?.id} className="opacity-75 hover:opacity-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{deposit?.client_name}</div>
                            <div className="text-xs text-gray-500">{deposit?.client_phone}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {deposit?.client_address}{deposit?.client_city ? `, ${deposit?.client_city}` : ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {getBarrelTypeLabel(deposit?.barrel_type)} × {deposit?.quantity}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {deposit?.planned_collection_date ? new Date(deposit?.planned_collection_date)?.toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td className="px-4 py-3">{getStatusBadge(deposit?.collection_status)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {deposit?.archived_at ? new Date(deposit?.archived_at)?.toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setDetailDeposit(deposit)}
                                title="Voir détails"
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setActionModal({ deposit, actionType: 'delete' })}
                                title="Supprimer définitivement"
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detailDeposit && (
        <DepotDetailModal
          deposit={detailDeposit}
          drivers={drivers}
          onClose={() => setDetailDeposit(null)}
        />
      )}

      {/* Edit Modal */}
      {editDeposit && (
        <DepotEditModal
          deposit={editDeposit}
          drivers={drivers}
          onClose={() => setEditDeposit(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Action Modal (Cancel / Archive / Delete) */}
      {actionModal && (
        <DepotActionModal
          deposit={actionModal?.deposit}
          actionType={actionModal?.actionType}
          onClose={() => setActionModal(null)}
          onConfirm={handleAction}
        />
      )}

      {/* New Deposit Modal */}
      {showNewDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Nouveau Dépôt de Barriques & Cartons</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom du client *</label>
                    <input type="text" value={newDeposit?.customerName} onChange={(e) => setNewDeposit({...newDeposit, customerName: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                    <input type="tel" value={newDeposit?.customerPhone} onChange={(e) => setNewDeposit({...newDeposit, customerPhone: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse *</label>
                  <input type="text" value={newDeposit?.customerAddress} onChange={(e) => setNewDeposit({...newDeposit, customerAddress: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville *</label>
                  <input type="text" value={newDeposit?.customerCity} onChange={(e) => setNewDeposit({...newDeposit, customerCity: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de contenant *</label>
                    <select value={newDeposit?.barrelType} onChange={(e) => setNewDeposit({...newDeposit, barrelType: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                      <optgroup label="Barriques">
                        <option value="noir_270l">Barrique Noire 270L</option>
                        <option value="bleu_220l">Barrique Bleue 220L</option>
                      </optgroup>
                      {cartonInventory?.length > 0 && (
                        <optgroup label="Cartons">
                          {cartonInventory?.map(carton => (
                            <option key={carton?.id} value={carton?.id}>{carton?.cartonType || carton?.description || `Carton ${carton?.id?.slice(0, 6)}`}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                    <input type="number" min="1" value={newDeposit?.quantity} onChange={(e) => setNewDeposit({...newDeposit, quantity: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                    <select value={newDeposit?.priority} onChange={(e) => setNewDeposit({...newDeposit, priority: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                      <option value="low">Basse</option>
                      <option value="normal">Normale</option>
                      <option value="high">Haute</option>
                      <option value="urgent">Urgente</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chauffeur assigné</label>
                  <select value={newDeposit?.driverId} onChange={(e) => setNewDeposit({...newDeposit, driverId: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                    <option value="">Non assigné</option>
                    {drivers?.map(driver => (
                      <option key={driver?.id} value={driver?.id}>{driver?.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date planifiée *</label>
                  <input type="date" value={newDeposit?.scheduledDate} onChange={(e) => setNewDeposit({...newDeposit, scheduledDate: e?.target?.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes de livraison</label>
                  <textarea value={newDeposit?.deliveryNotes} onChange={(e) => setNewDeposit({...newDeposit, deliveryNotes: e?.target?.value})} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowNewDepositModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
                <button onClick={handleCreateDeposit} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Créer le Dépôt</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Route Optimization Modal */}
      {showRouteOptimization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Map className="w-6 h-6 text-green-600" />
                Optimisation de Route
              </h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>{selectedDeposits?.length} dépôts sélectionnés</strong> pour optimisation de route.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assigner à un chauffeur</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                    <option value="">Sélectionner un chauffeur...</option>
                    {drivers?.map(driver => (
                      <option key={driver?.id} value={driver?.id}>{driver?.name}</option>
                    ))}
                  </select>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Dépôts sélectionnés:</h3>
                  <div className="space-y-2">
                    {deposits?.filter(d => selectedDeposits?.includes(d?.id))?.map((deposit, index) => (
                      <div key={deposit?.id} className="flex items-center gap-3 bg-white p-3 rounded border border-gray-200">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-semibold">{index + 1}</div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{deposit?.client_name}</div>
                          <div className="text-sm text-gray-500">{deposit?.client_address}, {deposit?.client_city}</div>
                        </div>
                        <div className="text-sm text-gray-600">{deposit?.quantity} × {getBarrelTypeLabel(deposit?.barrel_type)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowRouteOptimization(false); setSelectedDeposits([]); }} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
                <button onClick={() => { showToast('success', 'Fonctionnalité d\'optimisation de route en développement'); setShowRouteOptimization(false); }} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Optimiser et Assigner</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}